// Layer icons, from the dashboard redesign handoff.
//
// Inline SVG rather than an icon font or sprite sheet: fourteen glyphs at a few
// hundred bytes each is smaller than any font file, needs no extra request, and
// keeps the colours in the markup where the design specifies them per layer.
//
// The interior details are cut out in #161f2b — the panel background — rather
// than stroked. That only reads correctly against an opaque panel, which is why
// the panel background moved from 0.88 to 0.96 alpha along with these.
//
// Sizes are set by CSS (.lc-icon), so the viewBox is the only geometry here.

import { festivalColour } from './festival-colours.ts';

/**
 * The five-pointed star, shared by the Events dock button and every festival
 * row. One path, so a festival pin and the panel it lives in cannot drift into
 * two different stars.
 */
const STAR = 'M12 2.5l2.6 6.2 6.7.5-5.1 4.4 1.6 6.5L12 16.6l-5.8 3.5 1.6-6.5-5.1-4.4 6.7-.5z';

/** Every glyph the panels can draw. */
export type LayerIconName =
  | 'aircraft'
  | 'broadband'
  | 'cameras'
  | 'incidents'
  | 'roadworks'
  | 'roadworksFuture'
  | 'busStops'
  | 'coaches'
  | 'flood'
  | 'fringe'
  | 'fuel'
  | 'news'
  | 'power'
  | 'river'
  | 'ships'
  | 'taxi'
  | 'environment'
  | 'trains'
  | 'subwayStops'
  | 'tram'
  | 'tramStops'
  | 'voiBikes'
  | 'water'
  | 'waves'
  | 'weather';

const ICONS: Record<LayerIconName, string> = {
  aircraft: '<svg  viewBox="0 0 24 24"><path d="M12 2l1.6 5.4L21 11v2l-7.2-2.2.8 5.6 2.6 1.8v1.6l-3.4-1-1-.3-1 .3-3.4 1v-1.6l2.6-1.8.8-5.6L3 13v-2l7.4-3.6z" fill="#3ba7d9"/></svg>',
  broadband: '<svg  viewBox="0 0 24 24" fill="none" stroke="#8b8f96" stroke-width="1.8" stroke-linecap="round"><path d="M4 10a12 12 0 0 1 16 0"/><path d="M7.5 13.5a7.5 7.5 0 0 1 9 0"/><circle cx="12" cy="18" r="1.6" fill="#8b8f96" stroke="none"/></svg>',
  // A roadside camera: housing on a pole, rotated the way the real ones sit.
  // Steel grey, not amber — the Roads box already spends amber on the cone and
  // the warning triangle, and a camera is not a hazard. Same grey as weather.
  cameras: '<svg  viewBox="0 0 24 24"><rect x="11" y="12" width="2.2" height="9.4" rx="1.1" fill="#9fb4c7"/><rect x="6" y="18.6" width="12" height="2.6" rx="1.3" fill="#9fb4c7"/><g transform="rotate(-12 12 9)"><rect x="4.6" y="5.6" width="11.6" height="6.4" rx="1.8" fill="#9fb4c7"/><rect x="16.2" y="7.2" width="3.1" height="3.2" rx="1" fill="#9fb4c7"/><rect x="6.4" y="7.4" width="5.6" height="2.6" rx=".6" fill="#161f2b"/><circle cx="17.7" cy="8.8" r="1.1" fill="#161f2b"/></g></svg>',
  busStops: '<svg  viewBox="0 0 24 24"><line x1="7" y1="3" x2="7" y2="21" stroke="#8b8f96" stroke-width="1.6"/><rect x="7" y="4" width="12" height="7" fill="#8b8f96"/></svg>',
  coaches: '<svg  viewBox="0 0 24 24"><rect x="3" y="6" width="18" height="11" rx="2" fill="#e2574c"/><rect x="5" y="9" width="14" height="2.5" fill="#161f2b"/><circle cx="7.5" cy="18.5" r="1.6" fill="#e2574c"/><circle cx="16.5" cy="18.5" r="1.6" fill="#e2574c"/></svg>',
  flood: '<svg  viewBox="0 0 24 24"><path d="M12 3 2 20h20z" fill="#d9a441"/><line x1="12" y1="9" x2="12" y2="14" stroke="#161f2b" stroke-width="1.6" stroke-linecap="round"/><circle cx="12" cy="16.8" r="0.9" fill="#161f2b"/></svg>',
  // The festival star, in the Fringe's pink. Kept as a named glyph because the
  // Events dock button stands for the whole panel rather than one festival;
  // the per-festival rows recolour the same path in layerIcon().
  fringe: `<svg  viewBox="0 0 24 24"><path d="${STAR}" fill="#ff5c8a"/></svg>`,
  fuel: '<svg  viewBox="0 0 24 24"><rect x="4" y="3" width="10" height="18" rx="2" fill="#e2574c"/><rect x="6.5" y="6" width="5" height="4" rx="0.5" fill="#161f2b"/><path d="M14 8h2.5a1.5 1.5 0 0 1 1.5 1.5V16a1.5 1.5 0 0 0 3 0v-4.2a1.5 1.5 0 0 0-.44-1.06L18 8" stroke="#e2574c" stroke-width="1.6" fill="none" stroke-linecap="round" stroke-linejoin="round"/><circle cx="9" cy="17" r="1" fill="#161f2b"/></svg>',
  news: '<svg  viewBox="0 0 24 24"><rect x="3" y="4" width="15" height="16" rx="1.5" fill="#8b8f96"/><line x1="7" y1="8" x2="14" y2="8" stroke="#161f2b" stroke-width="1.4"/><line x1="7" y1="12" x2="14" y2="12" stroke="#161f2b" stroke-width="1.4"/><line x1="7" y1="16" x2="11" y2="16" stroke="#161f2b" stroke-width="1.4"/></svg>',
  // A cone, not a warning triangle. The triangle is already the flood and
  // weather-warning glyph, and three layers sharing one silhouette in a dock
  // of eight buttons is how a reader stops reading them.
  // The warning triangle every driver already reads — the toggle-row twin of
  // the map's 'incident' sprite, as the cone below is of 'roadworks'.
  incidents: '<svg  viewBox="0 0 24 24"><path d="M12 3.2 22.2 20.4H1.8z" fill="#f0a93b"/><rect x="10.9" y="9" width="2.2" height="6" rx="1.1" fill="#161f2b"/><circle cx="12" cy="17.6" r="1.3" fill="#161f2b"/></svg>',
  roadworks: '<svg  viewBox="0 0 24 24"><path d="M11.1 4h1.8l4.6 15H6.5z" fill="#f0a93b"/><path d="M9.6 9h4.8l.6 2H9z" fill="#161f2b"/><path d="M8.6 14h6.8l.6 2H8z" fill="#161f2b"/><rect x="4" y="19" width="16" height="2.2" rx="1.1" fill="#f0a93b"/></svg>',
  // The same cone with a clock in front, matching the map sprite — planned
  // works and current works are one object at two times, so the panel row and
  // the dot on the map must not tell different stories about which is which.
  roadworksFuture: '<svg  viewBox="0 0 24 24"><path d="M11.1 4h1.8l4.6 15H6.5z" fill="#f0a93b"/><path d="M9.6 9h4.8l.6 2H9z" fill="#161f2b"/><path d="M8.6 14h6.8l.6 2H8z" fill="#161f2b"/><rect x="4" y="19" width="16" height="2.2" rx="1.1" fill="#f0a93b"/><circle cx="16.6" cy="15.8" r="6" fill="#161f2b"/><circle cx="16.6" cy="15.8" r="4.6" fill="#f0a93b"/><path d="M16.6 12.7v3.1h2.5" stroke="#161f2b" stroke-width="1.5" fill="none" stroke-linecap="round" stroke-linejoin="round"/></svg>',
  power: '<svg  viewBox="0 0 24 24"><path d="M13 2 4 14h6l-1 8 9-12h-6z" fill="#f0a93b"/><line x1="3" y1="21" x2="21" y2="3" stroke="#161f2b" stroke-width="2.6"/><line x1="3" y1="21" x2="21" y2="3" stroke="#e2574c" stroke-width="1.6"/></svg>',
  river: '<svg  viewBox="0 0 24 24"><path d="M2 15c2 0 2-3 4-3s2 3 4 3 2-3 4-3 2 3 4 3 2-3 4-3v6H2z" fill="#3ba7d9"/><path d="M2 9c2 0 2-3 4-3s2 3 4 3 2-3 4-3 2 3 4 3 2-3 4-3" stroke="#3ba7d9" stroke-width="1.6" fill="none" stroke-linecap="round"/></svg>',
  ships: '<svg  viewBox="0 0 24 24"><path d="M5 14h14l-2 5H7z" fill="#3fb56a"/><line x1="12" y1="3" x2="12" y2="14" stroke="#3fb56a" stroke-width="1.6"/><path d="M12 5l5 3-5 2z" fill="#3fb56a"/></svg>',
  // Taxi ranks. The same FX4 face as the map sprite, so the row and the dot
  // are one object rather than two drawings of the same idea.
  taxi: '<svg  viewBox="0 0 24 24"><path d="M8.3 5.6h7.4c.9 0 1.5.5 1.7 1.3l1.5 9.5c.2 1.3-.5 2.1-1.7 2.1H6.8c-1.2 0-1.9-.8-1.7-2.1l1.5-9.5c.2-.8.8-1.3 1.7-1.3z" fill="#4cc2ff"/><rect x="8.4" y="2.4" width="7.2" height="3.4" rx="0.8" fill="#4cc2ff"/><path d="M9 7.3h6l.7 4.3H8.3z" fill="#161f2b"/><rect x="10" y="12.8" width="4" height="4.2" rx="0.8" fill="#161f2b"/><circle cx="7.4" cy="14.7" r="1.5" fill="#161f2b"/><circle cx="16.6" cy="14.7" r="1.5" fill="#161f2b"/></svg>',
  // A Subway station: the same drawing as the map's 'subway-stop' sprite — an
  // orange disc with the tunnel mouth knocked out — so the row and the mark
  // are one object, the treatment every baked-stop sprite gets.
  subwayStops: '<svg  viewBox="0 0 24 24"><circle cx="12" cy="12" r="9.5" fill="#ff8c1a"/><path d="M8 9.5a4 4 0 0 1 8 0V17H8z" fill="#161f2b"/></svg>',
  // A tram: the train's cousin — same purple, but a pantograph instead of
  // rails, so the two rows never read as duplicates.
  tram: '<svg  viewBox="0 0 24 24"><path d="M8 2h8M12 2v3" stroke="#a468d6" stroke-width="1.5" stroke-linecap="round" fill="none"/><rect x="6" y="5" width="12" height="13" rx="2.5" fill="#a468d6"/><rect x="8" y="8" width="8" height="4" rx="0.8" fill="#161f2b"/><circle cx="9.5" cy="15.2" r="1" fill="#161f2b"/><circle cx="14.5" cy="15.2" r="1" fill="#161f2b"/><path d="M9 18l-1.5 3.5M15 18l1.5 3.5" stroke="#a468d6" stroke-width="1.6" stroke-linecap="round"/></svg>',
  // A tram stop: the tram beside a shelter, under the wire. `tram` already
  // means the vehicle, so the platform is what makes this the place it stops —
  // the same distinction bus stops draw with a flag rather than a bus. The same
  // drawing as the map's 'tram-stop' sprite, so the row and the dot on the map
  // are one object rather than two pictures of the same idea.
  tramStops: '<svg  viewBox="0 0 24 24"><path d="M2 3.6h20M8 3.6v3" stroke="#a468d6" stroke-width="1.5" stroke-linecap="round" fill="none"/><rect x="3" y="6.6" width="10" height="10.4" rx="2.2" fill="#a468d6"/><rect x="5" y="8.8" width="6" height="3.2" rx="0.6" fill="#161f2b"/><circle cx="6.1" cy="15" r="0.85" fill="#161f2b"/><circle cx="9.9" cy="15" r="0.85" fill="#161f2b"/><rect x="15.4" y="6.6" width="2.4" height="15" rx="1.2" fill="#a468d6"/><rect x="15.4" y="6.6" width="6.6" height="2.6" rx="1.3" fill="#a468d6"/></svg>',
  trains: '<svg  viewBox="0 0 24 24"><rect x="5" y="3" width="14" height="14" rx="3" fill="#a468d6"/><rect x="7" y="6" width="10" height="3" fill="#161f2b"/><circle cx="9" cy="13.5" r="1" fill="#161f2b"/><circle cx="15" cy="13.5" r="1" fill="#161f2b"/><path d="M8 17l-2 4M16 17l2 4" stroke="#a468d6" stroke-width="1.6" stroke-linecap="round"/></svg>',
  voiBikes: '<svg  viewBox="0 0 24 24" fill="none" stroke="#8b8f96" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="6" cy="17" r="3.2"/><circle cx="18" cy="17" r="3.2"/><path d="M6 17l4-9h4l4 9M10 8h3M9 17h9"/></svg>',
  // The dock glyph for the Environment box. A leaf rather than any of the
  // layers inside it: river, waves and weather are all taken by rows in that
  // same box, and a button wearing one of its own children's glyphs reads as
  // a shortcut to that child rather than as the box.
  environment: '<svg  viewBox="0 0 24 24"><path d="M20 4c0 8.5-4.2 13-9.2 13A5.8 5.8 0 0 1 5 11.2C5 6.6 10.4 4 20 4z" fill="#3fb56a"/><path d="M17 7C12 9.5 8.6 13.6 7 21" stroke="#161f2b" stroke-width="1.6" fill="none" stroke-linecap="round"/></svg>',
  water: '<svg  viewBox="0 0 24 24"><path d="M12 3c3 4 6 7.2 6 10.8A6 6 0 0 1 6 13.8C6 10.2 9 7 12 3z" fill="#3ba7d9"/></svg>',
  waves: '<svg  viewBox="0 0 24 24"><path d="M2 17c2.5 0 3.5-2 6-2s3.5 2 6 2 3.5-2 6-2v5H2z" fill="#2b7fb8"/><path d="M2 12c2.5 0 3.5-4 6-4s3.5 4 6 4 3.5-4 6-4" stroke="#4fc3f7" stroke-width="1.8" fill="none" stroke-linecap="round"/></svg>',
  weather: '<svg  viewBox="0 0 24 24"><path d="M7 16a4 4 0 0 1 .3-8 5 5 0 0 1 9.6 1.6A3.5 3.5 0 0 1 16.5 16z" fill="#9fb4c7"/></svg>',
};

/**
 * Which glyph a layer draws.
 *
 * Keyed by the provider id the backend sends and by the vehicle `kind` the
 * frontend owns, because the panels list both and neither knows about the
 * other. Anything unmapped falls through to the emoji the backend already
 * sends, so a new provider appears with a reasonable icon rather than a gap.
 */
const BY_LAYER: Record<string, LayerIconName> = {
  // Vehicle layers, owned by the frontend.
  aircraft: 'aircraft',
  vessels: 'ships',
  buses: 'coaches',
  trains: 'trains',
  stops: 'busStops',
  // Siblings: these toggles split one baked payload and one fetch, so they
  // are listed together rather than each beside its own kind of vehicle.
  'tram-stops': 'tramStops',
  'subway-stops': 'subwayStops',
  // Providers, keyed by the id the backend uses in /api/v1/layers — which is
  // not always the label: Broadband is `telecoms` and Weather warnings is
  // `warnings`.
  cameras: 'cameras',
  incidents: 'incidents',
  roadworks: 'roadworks',
  'roadworks-future': 'roadworksFuture',
  power: 'power',
  rivers: 'river',
  waves: 'waves',
  fuel: 'fuel',
  water: 'water',
  telecoms: 'broadband',
  floods: 'flood',
  warnings: 'weather',
  fubar: 'news',
  voi: 'voiBikes',
  taxis: 'taxi',
  // No `fringe` entry: the festivals are one row each, keyed `festival:<id>`,
  // and layerIcon() colours their star from the festival rather than looking
  // one up here. The `fringe` glyph below is still drawn — the Events dock
  // button asks for it by name, and it stands for the panel, not a layer.
};

/**
 * The icon markup for a layer, or '' when there is no designed glyph for it.
 *
 * Returns markup rather than a name so callers can drop it straight into the
 * row template; an empty string is a legitimate answer and renders as nothing.
 */
export function layerIcon(id: string): string {
  /*
   * A festival row draws its own star, in its own colour.
   *
   * Not in BY_LAYER because there is no fixed list to put there: the rows that
   * exist are whichever festivals are on today, and the glyph is the shared
   * star recoloured. Same path as ICONS.fringe — deliberately the same shape,
   * so the row and the map pin read as the same thing — with the fill swapped.
   */
  if (id.startsWith('festival:')) {
    const colour = festivalColour(id.slice('festival:'.length));
    return `<svg  viewBox="0 0 24 24"><path d="${STAR}" fill="${colour}"/></svg>`;
  }
  const name = BY_LAYER[id];
  return name ? ICONS[name] : '';
}

/**
 * A glyph by its own name, for chrome that is not a layer row.
 *
 * The dock buttons stand for a whole panel rather than a layer, so they pick a
 * glyph directly instead of going through BY_LAYER — where the key is the
 * backend's provider id and 'weather' is spelled 'warnings'.
 */
export function icon(name: LayerIconName): string {
  return ICONS[name];
}

/** Whether a designed glyph exists, for callers that need a fallback. */
export function hasLayerIcon(id: string): boolean {
  return id in BY_LAYER;
}
